import { Table, TableContainer, TablePagination } from '@material-ui/core';
import { Pricing, PricingRange } from '@nemuru-eng/js-api-client';
import React, { useState } from 'react';

import { AddPricingSpecInLenderContextDialog } from '@/components/modules/AgentDetail/ProvidersDetails/components/AgentPricings/components/PricingSpecsTable/components/AddPricingSpecInLenderContextDialog/AddPricingSpecInLenderContextDialog';
import { PricingSpecTableRow } from '@/components/modules/AgentDetail/ProvidersDetails/components/AgentPricings/components/PricingSpecsTable/components/PricingSpecTableRow';
import { RemovePricingSpecFromPricingDialog } from '@/components/modules/AgentDetail/ProvidersDetails/components/AgentPricings/components/PricingSpecsTable/components/RemovePricingSpecFromPricingDialog/RemovePricingSpecFromPricingDialog';
import { RemovePricingSpecInLenderContextDialog } from '@/components/modules/AgentDetail/ProvidersDetails/components/AgentPricings/components/PricingSpecsTable/components/RemovePricingSpecInLenderContextDialog/RemovePricingSpecInLenderContextDialog';
import { usePricingSpecsTable } from '@/components/modules/AgentDetail/ProvidersDetails/components/AgentPricings/components/PricingSpecsTable/hooks/usePricingSpecsTable';
import { PricingSpecsTableAction } from '@/components/modules/AgentDetail/ProvidersDetails/components/AgentPricings/components/PricingSpecsTable/types/PricingSpecsTableAction';
import { CustomHeader } from '@/components/shared/components/CustomTable/CustomHeader';
import { useDefaultTable } from '@/components/shared/hooks/useDefaultTable';
import { useDialog } from '@/components/shared/hooks/useDialog';

interface PricingSpecsTableProps {
  pricing: Pricing;
  editModeAllowed?: boolean;
  refreshData: () => Promise<unknown>;
}

export const PricingSpecsTable = ({ pricing, editModeAllowed, refreshData }: PricingSpecsTableProps) => {
  const tableElementId = 'pricing-specs-table-container';
  const { classes, stickyCellStyle } = useDefaultTable({
    tableElementId,
  });
  const { opened, toggleDialog, setOpened } = useDialog();
  const { columns } = usePricingSpecsTable(pricing);

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [rowPricingAction, setRowPricingAction] = useState<PricingSpecsTableAction | undefined>(undefined);
  const [selectedPricingRange, setSelectedPricingRange] = useState<PricingRange | undefined>(undefined);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowPricingAction = (action: PricingSpecsTableAction, pricingRange: PricingRange) => {
    setRowPricingAction(action);
    setSelectedPricingRange(pricingRange);
    toggleDialog();
  };

  pricing.ranges.sort((a, b) => a.rangePrincipalMax - b.rangePrincipalMax);
  pricing.ranges.sort((a, b) => (a.rangePeriodMin && b.rangePeriodMin ? a.rangePeriodMin - b.rangePeriodMin : 0));

  return (
    <>
      <TableContainer id={tableElementId}>
        <Table size={'small'}>
          <CustomHeader tableProps={{ classes, stickyCellStyle }} columns={columns} />
          {pricing.ranges.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((range: PricingRange) => (
            <PricingSpecTableRow
              key={pricing.id}
              pricingRange={range}
              pricing={pricing}
              handleRowPricingAction={handleRowPricingAction}
              editModeAllowed={editModeAllowed}
            />
          ))}
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={pricing.ranges.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      {rowPricingAction === PricingSpecsTableAction.ADD_PRICING_SPEC_IN_LENDER_CONTEXT && selectedPricingRange && (
        <AddPricingSpecInLenderContextDialog
          opened={opened}
          setOpened={setOpened}
          toggleDialog={toggleDialog}
          pricingRange={selectedPricingRange}
          refreshData={refreshData}
        />
      )}
      {rowPricingAction === PricingSpecsTableAction.REMOVE_PRICING_SPEC_IN_LENDER_CONTEXT &&
        selectedPricingRange &&
        selectedPricingRange?.lenderEarningsFees !== null && (
          <RemovePricingSpecInLenderContextDialog
            opened={opened}
            setOpened={setOpened}
            toggleDialog={toggleDialog}
            pricingRange={selectedPricingRange}
            refreshData={refreshData}
          />
        )}
      {rowPricingAction === PricingSpecsTableAction.REMOVE_PRICING_SPEC_FROM_PRICING && selectedPricingRange && (
        <RemovePricingSpecFromPricingDialog
          opened={opened}
          setOpened={setOpened}
          toggleDialog={toggleDialog}
          pricing={pricing}
          pricingRange={selectedPricingRange}
        />
      )}
    </>
  );
};
